import React from 'react';
import styles from '../../styles/RagIndicatorRisk.module.scss';
import { TooltipHost, TooltipOverflowMode } from '@fluentui/react/lib/Tooltip';
import { IEntity, IRiskAppHeatMapColor } from '../../types';

interface IRiskCellInfo {
    colorHex: string;
    cellTitle: string | null;
    riskScore: number;
}

const calculateRiskScore = (
    probability: number,
    impact: number,
    formula?: string
): number => {

    if (!formula) {
        return probability * impact;
    }

    try {
        const expression = formula
            .replace(/P/g, probability.toString())
            .replace(/I/g, impact.toString());

        const result = Function(`return ${expression}`)();

        return Number(result);
    }
    catch {
        return probability * impact;
    }
};

const getRiskCellInfo = (
    probability: number | null,
    impact: number | null,
    heatMapColors: IRiskAppHeatMapColor[],
    riskScoreFormula?: string | null
): IRiskCellInfo => {

    if (
        probability == null ||
        impact == null ||
        !heatMapColors?.length
    ) {
        return {
            colorHex: '#D3D3D3',
            cellTitle: null,
            riskScore: 0
        };
    }

    const colorConfig = heatMapColors.find(c =>
        c.ImpactLevel === impact &&
        c.ProbabilityLevel === probability
    );

    const riskScore = calculateRiskScore(
        probability,
        impact,
        riskScoreFormula ?? undefined
    );

    return {
        colorHex: colorConfig?.ColorHex ?? '#D3D3D3',
        cellTitle: colorConfig?.CellTitle ?? null,
        riskScore
    };
};

export interface IRagIndicatorForRiskProps {
    // color?: string; // hex background color
    label?: string;

    impactLevelID: number;
    probabilityID: number;
    riskScoreFormula: string | null;
    riskAppHeatMapColors: IRiskAppHeatMapColor[];
}



// Utility: decide whether text should be white or black based on luminance
const getContrastTextColor = (hexColor: string): string => {
    if (!hexColor) return '#000'; // fallback
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#000' : '#FFF'; // light bg -> black text, dark bg -> white text
};

export const RagIndicatorForRisk = ({ label, impactLevelID, probabilityID, riskScoreFormula, riskAppHeatMapColors }: IRagIndicatorForRiskProps): React.ReactElement => {


    const displayLabel = label || 'To be completed';


    const convertToNumber = (val: any): number | null => {
        const num = parseInt(val, 10);
        return isNaN(num) ? null : num;
    };

    const pID = convertToNumber(probabilityID);
    const iID = convertToNumber(impactLevelID);

    const { riskScore, colorHex, cellTitle } = getRiskCellInfo(
        pID,
        iID,
        riskAppHeatMapColors,
        riskScoreFormula
    );

    const backgroundColor = colorHex || '#B0B0B0'; // grey fallback
    //const textColor = getContrastTextColor(backgroundColor);
    const textColor = !colorHex ? '#FFF' : getContrastTextColor(backgroundColor);
    const textShadow = !colorHex ? '0 1px 2px rgba(0,0,0,0.6)' : 'none';

    return (
        <div className={styles.ragIndicator}>
            <div
                className={styles.indicator}
                style={{
                    backgroundColor,
                    color: textColor,
                    textShadow: textShadow,
                }}
            >
                <div className={styles.leftColumn}>
                    {cellTitle}
                </div>
                <div className={styles.rightColumn}>
                    <div className={styles.riskScoreLine}>Risk Score: {riskScore}</div>
                    <TooltipHost
                        content={displayLabel}
                        overflowMode={TooltipOverflowMode.Parent}
                    >
                        <div className={styles.labelLine}>{displayLabel}</div>
                    </TooltipHost>
                </div>
            </div>
        </div>
    );
};
